import { useCallback, useState, useEffect } from 'react';
import { useDropzone, Accept, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import styles from './FileDropzone.module.scss';
import { DownloadIcon } from '../../icons/DownloadIcon';

interface FileDropzoneProps {
  onFileSelected: (file: File | null) => void;
  initialFile?: File | null;
  acceptedFileTypes?: Accept;
  maxFileSize?: number;
  helperText?: string;
  fullWidthPreview?: boolean;
}

export const FileDropzone = ({
  onFileSelected,
  initialFile = null,
  acceptedFileTypes = { 'image/jpeg': [], 'image/png': [], 'application/pdf': [] },
  maxFileSize = 25 * 1024 * 1024,
  helperText,
  fullWidthPreview = false,
}: FileDropzoneProps) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(initialFile);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedFile && selectedFile.type.startsWith('image/')) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [selectedFile]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0] || null;
    setSelectedFile(file);
    setErrorMsg(null);
    onFileSelected(file);
  }, [onFileSelected]);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    const rejection = fileRejections[0];
    if (!rejection) return;
    const error = rejection.errors[0];
    if (error?.code === 'file-too-large') {
      setErrorMsg(t('landing.FileDropzone.errorFileTooLarge', { size: maxFileSize / (1024 * 1024) }));
    } else if (error?.code === 'file-invalid-type') {
      setErrorMsg(t('landing.FileDropzone.errorInvalidType'));
    } else {
      setErrorMsg(t('landing.FileDropzone.errorGeneric'));
    }
    setSelectedFile(null);
    onFileSelected(null);
  }, [t, maxFileSize, onFileSelected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    accept: acceptedFileTypes,
    multiple: false,
    maxSize: maxFileSize,
  });

  return (
    <div>
      <div {...getRootProps()} className={`${styles['dropzone']} ${errorMsg ? styles['dropzone--error'] : ''}`}>
        <input {...getInputProps()} />

        {!previewUrl && (
          <div className={styles['dropzone__icon']}>
            <DownloadIcon width={20} />
          </div>
        )}

        {isDragActive ? (
          <p className={styles['dropzone__text']}>{t('landing.FileDropzone.dropFileHere')}</p>
        ) : selectedFile ? (
          previewUrl ? (
            <img 
              src={previewUrl} 
              alt={selectedFile.name} 
              className={styles['dropzone__preview-image']} 
              style={fullWidthPreview ? { maxHeight: '600px', maxWidth: '100%', width: '100%', objectFit: 'contain' } : undefined}
            />
          ) : (
            <p className={styles['dropzone__text']}>{t('landing.FileDropzone.file')} {selectedFile.name}</p>
          )
        ) : (
          <p className={styles['dropzone__text']}>
            <span>{t('landing.FileDropzone.clickToUpload')}</span> {t('landing.FileDropzone.orDragHere')}
          </p>
        )}

        <p className={styles['dropzone__hint']}>
          {t('landing.FileDropzone.maxFileSize', { size: maxFileSize / (1024 * 1024) })}
        </p>
        {helperText && (
          <p className={styles['dropzone__hint']} style={{ marginTop: '4px', fontWeight: 500 }}>
            {helperText}
          </p>
        )}
      </div>

      {errorMsg && (
        <p className={styles['dropzone__error']}>{errorMsg}</p>
      )}
    </div>
  );
};